import type { Timestamp } from 'firebase/firestore'

// Listing type enum
export type ListingType = 'general' | 'vehicle' | 'fundraiser'

// Sale type
export type SaleType = 'buynow' | 'auction' | 'product'

// Listing status
export type ListingStatus = 'active' | 'sold' | 'expired' | 'unsold' | 'closed' | 'draft' | 'deleted'

// Item condition
export type Condition = 'new' | 'excellent' | 'good' | 'fair' | 'used'

// Fundraiser type
export type FundraiserType = 'auction' | 'products' | 'raffle' | 'sponsorship'

// Vehicle transmission
export type Transmission = 'Automatic' | 'Manual'

// Fuel type
export type FuelType = 'Petrol' | 'Diesel' | 'Electric' | 'Hybrid' | 'LPG'

// Base listing interface (all listings have these fields)
export interface BaseListing {
  id: string
  title: string
  description: string
  price: number | null
  wasPrice?: number // Original/previous price for discounted items
  priceDisplay?: string // e.g., "$550/week", "Price by Negotiation"
  images: string[]
  category: string
  listingType: ListingType
  saleType?: SaleType
  condition?: Condition
  brand?: string // Brand name for products
  location: string
  suburb?: string

  // Shipping & Payment
  shippingOptions?: string[] // ['Pickup', 'Courier', 'Post']
  paymentOptions?: string[] // ['Cash', 'Bank Transfer', 'Card']

  // Seller info
  sellerId: string
  sellerName: string
  sellerRating?: number
  sellerReviews?: number
  sellerVerified?: boolean
  sellerBusiness?: boolean

  // Agent info (dealership listings)
  agentName?: string
  agentRole?: string
  agentPhotoUrl?: string
  agentEmail?: string
  agentPhone?: string

  // E-commerce (storefront/nonprofit inventory)
  stock?: number           // Available quantity (undefined = classifieds mode)
  sold?: number            // Total units sold
  sku?: string             // Optional seller-assigned SKU

  // Metadata
  status: ListingStatus
  views: number
  createdAt: Timestamp
  updatedAt?: Timestamp
  expiresAt?: Timestamp
}

// Auction fields (added when saleType === 'auction')
export interface AuctionFields {
  saleType: 'auction'
  startingPrice: number
  currentBid?: number
  bidCount: number
  highBidderId?: string
  highBidderName?: string
  reservePrice?: number
  reserveMet?: boolean
  buyNowPrice?: number // Optional instant-purchase price that ends the auction
  // Auto-bid (proxy bidding)
  maxBid?: number
  maxBidderId?: string
  maxBidderName?: string
  lastBidAt?: Timestamp
  auctionEnded?: boolean
}

// Vehicle-specific fields
export interface VehicleFields {
  listingType: 'vehicle'
  make: string
  model: string
  year: number
  odometer: number
  colour: string
  bodyType?: string
  doors?: number
  engineCC?: number
  fuelType?: FuelType
  transmission?: Transmission
  previousOwners?: string
  wofExpiry?: string
  regoExpiry?: string
  stolenCheck?: 'Passed' | 'Failed' | 'Pending'
  damageCheck?: 'Passed' | 'Failed' | 'Pending'

  // Dealer import tracking (set by Cloud Functions)
  importSource?: 'autoplay' | 'motorcentral'
  importSourceId?: string
  importedAt?: Timestamp
}

// Fundraiser product
export interface FundraiserProduct {
  id: string
  name: string
  price: number
  description?: string
  image?: string
  stock?: number
  sold?: number
}

// Raffle prize
export interface RafflePrize {
  id: string
  name: string
  description?: string
  value?: number
  image?: string
}

// Sponsorship tier
export interface SponsorshipTier {
  id: string
  name: string
  amount: number
  description: string
  benefits: string[]
  limit?: number
  claimed?: number
}

// Fundraiser-specific fields
export interface FundraiserFields {
  listingType: 'fundraiser'
  fundraiserType: FundraiserType
  organizationName?: string
  organizationLogo?: string
  closingDate?: string
  targetAmount?: number
  amountRaised?: number
  backerCount?: number

  // Auction-specific
  bidIncrement?: number

  // Products
  products?: FundraiserProduct[]

  // Raffle
  ticketPrice?: number
  totalTickets?: number
  ticketsSold?: number
  maxTicketsPerPerson?: number
  prizes?: RafflePrize[]
  termsAndConditions?: string

  // Sponsorship
  tiers?: SponsorshipTier[]
  sponsorRecognition?: string

  // Payment & shipping
  deliveryOptions?: string[]
  paymentOptions?: string[]
  shippingOptions?: string[]
  shippingCost?: number
}

// Combined listing types
export type GeneralListing = BaseListing & { listingType: 'general' }
export type VehicleListing = BaseListing & VehicleFields
export type FundraiserListing = BaseListing & FundraiserFields
export type AuctionListing = BaseListing & AuctionFields

// Union type for all listings
export type Listing = GeneralListing | VehicleListing | FundraiserListing | (BaseListing & AuctionFields)

// Type guards
export function isVehicleListing(listing: Listing): listing is VehicleListing {
  return listing.listingType === 'vehicle'
}

export function isFundraiserListing(listing: Listing): listing is FundraiserListing {
  return listing.listingType === 'fundraiser'
}

export function isAuctionListing(listing: Listing): listing is AuctionListing {
  return listing.saleType === 'auction'
}

// Form data types (for creating/editing)
export interface CreateListingData {
  title: string
  description: string
  price?: number
  category: string
  listingType: ListingType
  saleType?: SaleType
  condition?: Condition
  location: string
  suburb?: string
  images: (File | string)[]
  // Auction fields
  auctionDuration?: number
  reservePrice?: number
  buyNowPrice?: number
  startingPrice?: number
  currentBid?: number
  bidCount?: number
  reserveMet?: boolean
  expiresAt?: Date
  [key: string]: unknown // Additional type-specific fields
}
